import { useEffect, useRef, useState } from "react";
import { ArrowLeft, Check, Eye, LayoutPanelTop, Plus, Save, Send } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";
import { Button, Textarea } from "../../components/ui";
import { editReminder, editWorkflowNode, getWorkflowDraft, saveWorkflowDraft, sendWorkflowMessage, WorkflowBuilderApiError, type SaveWorkflowRequest } from "./api";
import type { BuilderMode, ChatRequest, EditReminderRequest, EditWorkflowNodeRequest, WorkflowDraft } from "./models";
import { WorkflowBuilderCanvas } from "./WorkflowBuilderCanvas";
import { WorkflowPreviewCard } from "./WorkflowPreviewCard";
import { useBuilderIdentity } from "./useBuilderIdentity";
import { builderPreviewWorkflow, currentBuilderPreview, initialBuilderProjectId, usesProjectSources, visibleBuilderMessages, withoutClientResults } from "./builderPreviewContext";
import { WorkflowPreviewClientPicker } from "./WorkflowPreviewClientPicker";
import { DataScopePicker } from "../workflowData/DataScopePicker";
import { hasDataScope, projectScope, scopeKey, scopeProjectId, type DataScope } from "../workflowData/models";
import { ReminderPreview, ReminderStatus, ReminderTaskCard } from "./ReminderTaskCard";
import { useReminderStrings } from "./reminderStrings";
import styles from "./WorkflowBuilder.module.css";

export function WorkflowBuilderPage() {
    const navigate = useNavigate();
    const { draftId } = useParams<{ draftId: string }>();
    const { scope, switching } = useBuilderIdentity();
    return <BuilderWorkspace key={`${scope}:${draftId ?? "new"}`} draftId={draftId} switching={switching}
        onClose={() => navigate("/app")}
        onCreate={() => navigate("/app/workflows/new")}
        onDraftCreated={draft => navigate(`/app/workflows/${encodeURIComponent(draft.id)}`, { replace: true })} />;
}

type Props = {
    draftId?: string;
    organizationId?: string;
    initialDraft?: WorkflowDraft;
    switching: boolean;
    onClose: () => void;
    backLabel?: string;
    onCreate: () => void;
    onDraftCreated: (draft: WorkflowDraft) => void;
};
export function BuilderWorkspace({ draftId, organizationId, initialDraft, switching, onClose, backLabel, onCreate, onDraftCreated }: Props) {
    const { t, i18n } = useTranslation();
    const reminderWords = useReminderStrings();
    const { resolveToken } = useBuilderIdentity();
    const [draft, setDraft] = useState<WorkflowDraft | undefined>(initialDraft);
    const [mode, setMode] = useState<BuilderMode>("build");
    const [reminderPreviewOpen, setReminderPreviewOpen] = useState(false);
    const [selectedProjectId, setSelectedProjectId] = useState<string | null>(() => initialDraft ? initialBuilderProjectId(initialDraft) : null);
    const [selectedScope, setSelectedScope] = useState<DataScope | null>(() => initialDraft?.workflow.data_scope ?? initialDraft?.workflow.reminder?.project_summary?.data_scope ?? projectScope(initialDraft ? initialBuilderProjectId(initialDraft) : null));
    const [text, setText] = useState("");
    const [loading, setLoading] = useState(Boolean(draftId && !initialDraft));
    const [busy, setBusy] = useState(false);
    const [saving, setSaving] = useState(false);
    const [editingNode, setEditingNode] = useState(false);
    const [savedRevision, setSavedRevision] = useState<number>();
    const [pendingText, setPendingText] = useState("");
    const [error, setError] = useState("");
    const [loadAttempt, setLoadAttempt] = useState(0);
    const controllers = useRef(new Set<AbortController>());
    const pendingRequest = useRef<{ fingerprint: string; body: ChatRequest }>();
    const pendingSave = useRef<SaveWorkflowRequest>();
    const savingRequest = useRef(false);
    const sending = useRef(false);
    const editingRequest = useRef(false);
    const contextEpoch = useRef(0);
    const loadedDraft = useRef(Boolean(initialDraft));
    const scrollEnd = useRef<HTMLDivElement>(null);
    const previewCard = useRef<HTMLElement>(null);
    const composer = useRef<HTMLTextAreaElement>(null);
    useEffect(() => () => { controllers.current.forEach(controller => controller.abort()); }, []);
    useEffect(() => {
        if (!draftId || switching) return;
        if (initialDraft?.id === draftId && loadAttempt === 0) return;
        const controller = new AbortController();
        controllers.current.add(controller);
        const epoch = contextEpoch.current;
        setLoading(true); setError(""); setDraft(undefined);
        void (async () => {
            try {
                const token = await resolveToken();
                if (controller.signal.aborted || epoch !== contextEpoch.current) return;
                const result = await getWorkflowDraft(draftId, token, controller.signal, organizationId);
                if (!controller.signal.aborted && epoch === contextEpoch.current) {
                    setDraft(result);
                    if (!loadedDraft.current) {
                        setSelectedProjectId(initialBuilderProjectId(result));
                        setSelectedScope(result.workflow.data_scope ?? result.workflow.reminder?.project_summary?.data_scope ?? projectScope(initialBuilderProjectId(result)));
                        loadedDraft.current = true;
                    }
                    if (result.messages[result.messages.length - 1]?.intent === "preview") setMode("preview");
                }
            } catch (caught: unknown) {
                if (!controller.signal.aborted && epoch === contextEpoch.current) setError(caught instanceof Error ? caught.message : t("workflowBuilder.loadError"));
            } finally {
                controllers.current.delete(controller);
                if (!controller.signal.aborted && epoch === contextEpoch.current) setLoading(false);
            }
        })();
        return () => controller.abort();
    }, [draftId, organizationId, initialDraft, resolveToken, loadAttempt, switching, t]);
    useEffect(() => {
        if (mode === "preview" && !pendingText && draft?.preview?.workflow_revision === draft?.workflow_revision && previewCard.current) {
            previewCard.current.scrollIntoView({ block: "start", behavior: "smooth" });
        } else scrollEnd.current?.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }, [draft?.messages.length, pendingText, mode, draft?.preview?.created_at, draft?.workflow_revision]);
    const locked = busy || saving || editingNode || loading || switching;
    const boxes = draft?.workflow.boxes ?? [];
    const isReminder = draft?.workflow.kind === "reminder";
    const projectSummary = draft?.workflow.reminder?.project_summary;
    const isReport = projectSummary?.output_format === "docx";
    const taskPreviewHelp = isReport ? reminderWords.reportPreviewHelp : reminderWords.summaryPreviewHelp;
    const noteMonitor = draft?.workflow.reminder?.note_monitor;
    const projectTask = projectSummary ?? noteMonitor;
    const requiresClient = usesProjectSources(draft?.workflow);
    const activeScope = requiresClient && !noteMonitor ? selectedScope : null;
    const activeProjectId = requiresClient ? noteMonitor ? selectedProjectId : scopeProjectId(activeScope) : null;
    const hasScope = hasDataScope(activeScope, activeProjectId);
    // The project list supplies choices, not authorization. Save and Preview
    // recheck access on the server without waiting for the full list to load.
    const canSave = (isReminder ? Boolean(draft.workflow.reminder) : boxes.length > 0) && (!projectTask || hasScope);
    const currentPreview = currentBuilderPreview(draft, activeProjectId, activeScope);

    const selectClient = (projectId: string | null) => {
        if (projectId === selectedProjectId) return;
        contextEpoch.current += 1;
        controllers.current.forEach(controller => controller.abort());
        controllers.current.clear();
        sending.current = false; savingRequest.current = false;
        pendingRequest.current = undefined; pendingSave.current = undefined;
        setSelectedProjectId(projectId);
        setDraft(current => current ? { ...current, preview: null, messages: current.messages.filter(message => message.project_id === null) } : current);
        setBusy(false); setSaving(false); setSavedRevision(undefined); setPendingText(""); setError("");
    };

    const selectScope = (dataScope: DataScope | null) => {
        if (scopeKey(dataScope) === scopeKey(selectedScope)) return;
        selectClient(scopeProjectId(dataScope));
        contextEpoch.current += 1;
        controllers.current.forEach(controller => controller.abort()); controllers.current.clear();
        sending.current = false; savingRequest.current = false; pendingRequest.current = undefined; pendingSave.current = undefined;
        setSelectedScope(dataScope);
        setDraft(current => current ? { ...current, preview: null, messages: current.messages.filter(message => message.project_id === null && !message.data_scope) } : current);
        setBusy(false); setSaving(false); setSavedRevision(undefined); setPendingText(""); setError("");
    };

    const send = async (message: string, requestedMode: BuilderMode = isReminder && !projectTask ? "build" : mode, selections?: Record<string, string>) => {
        if (locked || sending.current || savingRequest.current || editingRequest.current || !message.trim()) return;
        // Fixed-message reminders preview locally; project summaries run against the selected Project.
        if (isReminder && !projectTask) requestedMode = "build";
        if (requestedMode === "preview" && requiresClient && !hasScope) {
            setError(t(noteMonitor ? "workflowBuilder.selectProjectForPreview" : "workflowData.choose")); return;
        }
        const epoch = contextEpoch.current;
        const fingerprint = JSON.stringify({ message: message.trim(), mode: requestedMode, revision: draft?.revision, selections, projectId: activeProjectId, dataScope: activeScope });
        const body: ChatRequest = pendingRequest.current?.fingerprint === fingerprint ? pendingRequest.current.body : {
            message: message.trim(), mode: requestedMode, language: i18n.language, request_id: crypto.randomUUID(),
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            project_id: activeProjectId, data_scope: activeScope,
            ...(draft ? { expected_revision: draft.revision } : {}),
            ...(selections ? { preview_selections: selections } : {})
        };
        pendingRequest.current = { fingerprint, body };
        sending.current = true; setBusy(true); setPendingText(message.trim()); setError("");
        const controller = new AbortController(); controllers.current.add(controller);
        let authenticated = false;
        try {
            const token = await resolveToken();
            authenticated = true;
            if (controller.signal.aborted || epoch !== contextEpoch.current) return;
            const result = await sendWorkflowMessage(draft?.id, body, token, controller.signal, organizationId);
            if (controller.signal.aborted || epoch !== contextEpoch.current) return;
            pendingRequest.current = undefined;
            setDraft(result);
            if (result.workflow.data_scope ?? result.workflow.reminder?.project_summary?.data_scope) setSelectedScope(result.workflow.data_scope ?? result.workflow.reminder?.project_summary?.data_scope ?? null);
            if ((result.workflow.reminder?.project_summary || result.workflow.reminder?.note_monitor) && requestedMode !== "preview") {
                setSelectedProjectId(initialBuilderProjectId(result));
                setSelectedScope(result.workflow.data_scope ?? result.workflow.reminder?.project_summary?.data_scope ?? projectScope(initialBuilderProjectId(result)));
            }
            if (!selections) setText("");
            const last = result.messages[result.messages.length - 1];
            if (last?.intent === "preview") setMode("preview");
            if (last?.intent === "preview" && result.workflow.kind === "reminder" && !result.workflow.reminder?.project_summary && !result.workflow.reminder?.note_monitor) setReminderPreviewOpen(true);
            if (!draftId) onDraftCreated(result);
        } catch (caught: unknown) {
            if (controller.signal.aborted || epoch !== contextEpoch.current) return;
            if (!authenticated || (caught instanceof WorkflowBuilderApiError && [401, 403, 404].includes(caught.status))) {
                setDraft(current => withoutClientResults(current, activeProjectId));
            }
            if (caught instanceof WorkflowBuilderApiError && caught.status === 409 && draft) {
                try {
                    const latest = await getWorkflowDraft(draft.id, await resolveToken(), controller.signal, organizationId);
                    if (!controller.signal.aborted && epoch === contextEpoch.current) setDraft(latest);
                } catch { /* Retain the request and original conflict error for the user. */ }
            }
            if (!controller.signal.aborted) setError(caught instanceof Error ? caught.message : t("workflowBuilder.sendError"));
        } finally {
            controllers.current.delete(controller);
            if (!controller.signal.aborted && epoch === contextEpoch.current) {
                sending.current = false;
                setBusy(false); setPendingText("");
                if (!selections) composer.current?.focus();
            }
        }
    };

    const saveWorkflow = async () => {
        if (!draft || !canSave || locked || sending.current || savingRequest.current || editingRequest.current) return;
        const epoch = contextEpoch.current;
        const body = pendingSave.current?.expected_revision === draft.revision && pendingSave.current.project_id === (projectTask ? activeProjectId : undefined) && scopeKey(pendingSave.current.data_scope) === scopeKey(activeScope) ? pendingSave.current : {
            expected_revision: draft.revision, request_id: crypto.randomUUID(), data_scope: activeScope, ...(projectTask ? { project_id: activeProjectId } : {})
        };
        pendingSave.current = body;
        savingRequest.current = true;
        setSaving(true); setError(""); setSavedRevision(undefined);
        const controller = new AbortController(); controllers.current.add(controller);
        try {
            const token = await resolveToken();
            if (controller.signal.aborted || epoch !== contextEpoch.current) return;
            const result = await saveWorkflowDraft(draft.id, body, token, controller.signal, organizationId);
            if (controller.signal.aborted || epoch !== contextEpoch.current) return;
            pendingSave.current = undefined;
            setDraft(result); setSavedRevision(result.revision);
            if (result.workflow.reminder?.project_summary || result.workflow.reminder?.note_monitor) {
                setSelectedProjectId(initialBuilderProjectId(result));
                setSelectedScope(result.workflow.data_scope ?? result.workflow.reminder?.project_summary?.data_scope ?? projectScope(initialBuilderProjectId(result)));
            }
        } catch (caught: unknown) {
            if (controller.signal.aborted || epoch !== contextEpoch.current) return;
            if (caught instanceof WorkflowBuilderApiError && [401, 403, 404].includes(caught.status)) {
                setDraft(current => withoutClientResults(current, activeProjectId));
            }
            if (caught instanceof WorkflowBuilderApiError && caught.status === 409) {
                try {
                    const latest = await getWorkflowDraft(draft.id, await resolveToken(), controller.signal, organizationId);
                    if (!controller.signal.aborted && epoch === contextEpoch.current) setDraft(latest);
                } catch { /* Keep the visible draft and report the original save error. */ }
            }
            if (!controller.signal.aborted) setError(caught instanceof Error ? caught.message : t("workflowBuilder.saveError"));
        } finally {
            controllers.current.delete(controller);
            if (!controller.signal.aborted && epoch === contextEpoch.current) {
                savingRequest.current = false; setSaving(false);
            }
        }
    };

    const saveNode = async (body: EditWorkflowNodeRequest | EditReminderRequest): Promise<void> => {
        if (!draft || locked || sending.current || savingRequest.current || editingRequest.current) {
            throw new Error(t("workflowBuilder.nodeSaveError"));
        }
        const epoch = contextEpoch.current;
        const controller = new AbortController(); controllers.current.add(controller);
        editingRequest.current = true; setEditingNode(true); setError("");
        try {
            const token = await resolveToken();
            if (controller.signal.aborted || epoch !== contextEpoch.current) throw new Error(t("workflowBuilder.nodeSaveError"));
            const result = "reminder" in body
                ? await editReminder(draft.id, body, token, controller.signal, organizationId)
                : await editWorkflowNode(draft.id, body, token, controller.signal, organizationId);
            if (controller.signal.aborted || epoch !== contextEpoch.current) throw new Error(t("workflowBuilder.nodeSaveError"));
            pendingRequest.current = undefined; pendingSave.current = undefined;
            setDraft(result); setSavedRevision(undefined);
        } catch (caught: unknown) {
            if (!controller.signal.aborted && epoch === contextEpoch.current) {
                if (caught instanceof WorkflowBuilderApiError && [401, 403, 404].includes(caught.status)) {
                    setDraft(current => withoutClientResults(current, activeProjectId));
                }
                if (caught instanceof WorkflowBuilderApiError && caught.status === 409) {
                    try {
                        const latest = await getWorkflowDraft(draft.id, await resolveToken(), controller.signal, organizationId);
                        if (!controller.signal.aborted && epoch === contextEpoch.current) setDraft(latest);
                    } catch { /* Keep the editor input and report the original conflict. */ }
                }
            }
            throw caught instanceof Error ? caught : new Error(t("workflowBuilder.nodeSaveError"));
        } finally {
            controllers.current.delete(controller);
            if (!controller.signal.aborted && epoch === contextEpoch.current) {
                editingRequest.current = false; setEditingNode(false);
            }
        }
    };

    return <main className={styles.page}>
        <Button className={styles.backButton} variant="ghost" size="sm" iconOnly disabled={editingNode} aria-label={backLabel ?? t("workflowBuilder.back")} onClick={onClose}><ArrowLeft size={20} /></Button>
        <section className={styles.board} aria-label={t("workflowBuilder.boardTitle")} aria-busy={busy || saving || editingNode}>
            <div className={styles.boardHeader}>
                {requiresClient && !noteMonitor && <DataScopePicker value={selectedScope} disabled={loading || switching || editingNode} onChange={selectScope} />}
                {noteMonitor && <WorkflowPreviewClientPicker value={selectedProjectId}
                    currentName={projectTask?.project_name ?? currentPreview?.project_name} label={projectTask ? reminderWords.project : undefined}
                    disabled={loading || switching || editingNode} onChange={selectClient} />}
                <div className={styles.boardActions}>
                    <div className={styles.modeSwitch} role="group" aria-label={t("workflowBuilder.mode")}>
                        <Button size="sm" variant={mode === "build" ? "primary" : "ghost"} aria-pressed={mode === "build"} disabled={locked} leftIcon={<LayoutPanelTop size={15} />} onClick={() => setMode("build")}>{t("workflowBuilder.build")}</Button>
                        <Button size="sm" variant={mode === "preview" ? "primary" : "ghost"} aria-pressed={mode === "preview"} disabled={locked || !canSave} leftIcon={<Eye size={15} />} onClick={() => {
                            setMode("preview");
                            if (projectTask) void send(projectSummary?.instructions ?? draft?.workflow.reminder?.message ?? "Preview", "preview");
                            else if (isReminder) setReminderPreviewOpen(true);
                            else composer.current?.focus();
                        }}>{t("workflowBuilder.preview")}</Button>
                    </div>
                    <Button variant="ghost" size="sm" disabled={locked || !draft || !canSave}
                        isLoading={saving} leftIcon={<Save size={16} />} onClick={() => { void saveWorkflow(); }}>
                        {t(saving ? "workflowBuilder.saving" : "workflowBuilder.save")}
                    </Button>
                    <Button variant="ghost" size="sm" disabled={locked} leftIcon={<Plus size={16} />} onClick={() => {
                        if (draftId) onCreate(); else { pendingRequest.current = undefined; setText(""); setError(""); composer.current?.focus(); }
                    }}>{t("workflowBuilder.new")}</Button>
                </div>
            </div>
            {projectTask && !hasScope && <p className={styles.previewHint}>{(isReport ? reminderWords.reportChooseProject : reminderWords.summaryChooseProject)}</p>}
            {draft && isReminder ? <ReminderTaskCard draft={draft} disabled={locked} onEdit={saveNode}
                firstSaveHelp={organizationId ? t("organizationWorkflowBuilder.scheduledHelp") : undefined} /> : draft && boxes.length ? <WorkflowBuilderCanvas workflow={draft.workflow} draftId={draft.id}
                editor={{ revision: draft.revision, disabled: locked, saving: editingNode, onSave: saveNode }} /> : <p className={styles.emptyBoard}>{t("workflowBuilder.boardEmpty")}</p>}
            {!isReminder && draft?.schedule_state && <p className={styles.previewHint}><ReminderStatus state={draft.schedule_state} /> {reminderWords.activeOtherKind}</p>}
        </section>
        <div className={styles.conversation}>
            {loading && <p role="status">{t("workflowBuilder.loading")}</p>}
            {visibleBuilderMessages(draft, activeProjectId, activeScope).map(message => <article key={message.id} className={message.role === "user" ? styles.userMessage : styles.assistantMessage} aria-label={t(message.role === "user" ? "workflowBuilder.you" : "workflowBuilder.assistant")}>
                {message.role === "assistant" && <span className={styles.messageIntent}>{t(`workflowBuilder.intent_${message.intent}`)}</span>}
                <ReactMarkdown skipHtml>{message.content}</ReactMarkdown>
            </article>)}
            <div hidden={mode !== "preview"}>
            {currentPreview && draft && <WorkflowPreviewCard
                key={draft.id}
                previewRef={previewCard}
                workflow={builderPreviewWorkflow(draft.workflow)}
                preview={currentPreview}
                disabled={locked || !canSave}
                saving={saving}
                saveSucceeded={savedRevision === draft.revision}
                savedMessage={organizationId ? t("organizationWorkflowBuilder.scheduledSaved") : undefined}
                error={error}
                onSaveWorkflow={() => { void saveWorkflow(); }}
                onSelectionChange={(boxId, value) => {
                    const box = boxes.find(item => item.id === boxId);
                    const option = box?.options.find(item => item.value === value);
                    if (!box || !option) return;
                    const selections: Record<string, string> = {};
                    for (const result of currentPreview.boxes) {
                        if (result.selected_value !== null) selections[result.box_id] = result.selected_value;
                    }
                    selections[boxId] = value;
                    void send(t("workflowBuilder.selectionMessage", { box: box.title, value: option.label }), "preview", selections);
                }}
            />}
            </div>
            {mode === "preview" && (!isReminder || projectTask) && !currentPreview && <p className={styles.previewHint}>{projectSummary ? taskPreviewHelp : t(requiresClient ? "workflowBuilder.projectPreviewHelp" : "workflowBuilder.previewHelp")}</p>}
            {pendingText && <article className={styles.userMessage}>{pendingText}</article>}
            {busy && <p role="status" className={styles.working}>{t(mode === "preview" ? "workflowBuilder.running" : "workflowBuilder.thinking")}</p>}
            {error && <div role="alert" className={styles.error}><p>{error}</p>{!draft && draftId && <Button onClick={() => setLoadAttempt(value => value + 1)}>{t("workflowBuilder.retry")}</Button>}</div>}
            <div ref={scrollEnd} />
        </div>
        <form className={styles.composer} onSubmit={event => { event.preventDefault(); void send(text); }}>
            {draft && !busy && !saving && !editingNode && <div className={styles.composerHeading}>
                <small className={styles.saved}><Check size={13} />{isReminder || draft.schedule_state ? !draft.schedule_state ? reminderWords.draftSaved : draft.schedule_state.workflow_revision !== draft.workflow_revision ? reminderWords.changesSaved : organizationId ? t("organizationWorkflowBuilder.scheduledSaved") : reminderWords.saved : t("workflowBuilder.saved")}</small>
            </div>}
            <Textarea ref={composer} value={text} onChange={event => setText(event.target.value)} disabled={locked || Boolean(draftId && !draft)} maxLength={16000} rows={3}
                aria-label={t(mode === "preview" && (!isReminder || projectTask) ? "workflowBuilder.previewInput" : "workflowBuilder.buildInput")}
                placeholder={noteMonitor ? (i18n.language.startsWith("zh") ? "修改监测项目、Note 类型、统计范围或提醒阈值…" : "Change the project, Note type, counting scope or threshold…") : projectSummary ? mode === "preview" ? taskPreviewHelp : isReport ? reminderWords.reportChatPlaceholder : reminderWords.summaryChatPlaceholder : isReminder ? reminderWords.chatPlaceholder : t(mode === "preview" ? requiresClient ? "workflowBuilder.projectPreviewPlaceholder" : "workflowBuilder.previewPlaceholder" : "workflowBuilder.buildPlaceholder")}
                onKeyDown={event => { if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing) { event.preventDefault(); void send(text); } }} />
            <div className={styles.composerFooter}><Button type="submit" variant="primary" disabled={locked || !text.trim() || Boolean(draftId && !draft)} isLoading={busy} leftIcon={<Send size={16} />}>{t("workflowBuilder.send")}</Button></div>
        </form>
        {reminderPreviewOpen && isReminder && !projectTask && draft.workflow.reminder && <ReminderPreview title={draft.workflow.title} reminder={draft.workflow.reminder} onClose={() => setReminderPreviewOpen(false)} />}
    </main>;
}
